import React, { useEffect, useState } from 'react';
import { ReportHeader } from '../../header/report-header';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ScholarshipReportDb } from '../../../../api/types/reports/accountant/scholarship-report.type';
import { ScholarshipSideBarFilter } from './side-bar/scholarship-side-bar-filter';
import {
  ScholarshipReportFilters,
  getScholarshipReportOperation,
} from './operations/get-scholarship-report-operation';
import { formatDate } from '../../../../utils/date-format';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { StudentScholarshipsStatus } from '../../../../api/types/students/student-scholarships/student-scholarship-entity';
import { DataTablePageEvent } from 'primereact/datatable';
import { UpdateStatusScholarshipDialog } from './update-status-scholarship/update-status-scholarship-dialog';

export const ReportScholarship: React.FC = () => {
  const [data, setData] = useState<ScholarshipReportDb[]>([]);
  const [loading, setLoading] = useState(false);

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<ScholarshipReportFilters>({
    scholarshipSumStart: null,
    scholarshipSumEnd: null,
    createdAtStart: null,
    createdAtEnd: null,
    statuses: [],
  });

  const [selectedScholarship, setSelectedScholarship] =
    useState<ScholarshipReportDb | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const fetchReportData = async (
    payload: ScholarshipReportFilters,
    newOffset: number,
    newLimit: number,
  ) => {
    setFilters(payload);
    await getScholarshipReportOperation(
      payload,
      newOffset,
      newLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  const getStatusSeverity = (
    status: StudentScholarshipsStatus,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case StudentScholarshipsStatus.APPROVED:
        return 'success';
      case StudentScholarshipsStatus.PENDING:
        return 'warning';
      case StudentScholarshipsStatus.FAILED:
        return 'danger';
      case StudentScholarshipsStatus.CANCELLED:
        return 'info';
      default:
        return 'info';
    }
  };

  const getStatusLabel = (status: StudentScholarshipsStatus): string => {
    switch (status) {
      case StudentScholarshipsStatus.PENDING:
        return 'ממתינה';
      case StudentScholarshipsStatus.APPROVED:
        return 'מאושרת';
      case StudentScholarshipsStatus.FAILED:
        return 'נכשלה';
      case StudentScholarshipsStatus.CANCELLED:
        return 'בוטלה';
      default:
        return status;
    }
  };

  useEffect(() => {
    // initial: wait for user to pick filters
  }, []);

  const handleOnSearch = async (payload: ScholarshipReportFilters) => {
    setOffset(0);
    await fetchReportData(payload, 0, limit);
  };

  const onPageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    fetchReportData(filters, newOffset, newLimit);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title='דו"ח מלגות סטודנטים'
        description='דו\"ח זה מציג מידע אודות מלגות הסטודנטים, כולל סכום מלגה, סכום שנוצל ויתרה בהתאם לטווחי סכומים ולסטטוסים שנבחרו.'
        metadata={[
          {
            label: 'מטרת הדו"ח',
            value: 'מעקב אחר מלגות סטודנטים לפי סטטוס וסכומים',
          },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <ScholarshipSideBarFilter onSearch={handleOnSearch} />
        </div>

        <div style={{ flex: 1 }}>
          {showUpdateDialog && selectedScholarship && (
            <UpdateStatusScholarshipDialog
              visible={showUpdateDialog}
              onHide={() => {
                setShowUpdateDialog(false);
                setSelectedScholarship(null);
              }}
              scholarship={selectedScholarship}
              onStatusUpdated={() => {
                fetchReportData(filters, offset, limit);
                setShowUpdateDialog(false);
                setSelectedScholarship(null);
              }}
            />
          )}

          <DataTable
            value={data}
            loading={loading}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy
            rowsPerPageOptions={[10, 25, 50]}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתוני מלגות</div>
            }
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{ direction: 'rtl' }}
          >
            <Column
              field="uid"
              header="מזהה סטודנט"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              header="שם סטודנט"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: ScholarshipReportDb) => (
                <span>
                  {row.firstName} {row.lastName}
                </span>
              )}
            />
            <Column
              field="nationalId"
              header="תעודת זהות"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="phoneNumber"
              header="טלפון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="email"
              header="אימייל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="scholarshipId"
              header="מזהה מלגה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="scholarshipSum"
              header="סכום מלגה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: ScholarshipReportDb) =>
                `₪${row.scholarshipSum?.toLocaleString()}`
              }
            />
            <Column
              field="consumeSum"
              header="סכום שנוצל"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: ScholarshipReportDb) =>
                `₪${row.consumeSum?.toLocaleString()}`
              }
            />
            <Column
              header="יתרה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: ScholarshipReportDb) =>
                `₪${(row.scholarshipSum - row.consumeSum)?.toLocaleString()}`
              }
            />
            <Column
              field="studentScholarshipsStatus"
              header="סטטוס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: ScholarshipReportDb) => (
                <Tag
                  value={getStatusLabel(row.studentScholarshipsStatus)}
                  severity={getStatusSeverity(row.studentScholarshipsStatus)}
                />
              )}
            />
            <Column
              header="פעולות"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: ScholarshipReportDb) => (
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  outlined
                  severity="secondary"
                  onClick={() => {
                    setSelectedScholarship(row);
                    setShowUpdateDialog(true);
                  }}
                  tooltip="עדכון סטטוס מלגה"
                  tooltipOptions={{ position: 'top' }}
                />
              )}
            />
            <Column
              field="createdAt"
              header="תאריך יצירת מלגה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: ScholarshipReportDb) => formatDate(row.createdAt)}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
